import { useCallback, useEffect, useState } from 'react'
import plgNotificationsAPI from 'api/plgNotifications'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { goToCreditsBilling } from 'containers/Agents/creditNavigation'
import {
  selectBillingStatus,
  selectBillingStatusError,
  selectCompanyAchievements,
  selectIsAdminOrOwner,
  selectIsBillingDisabled,
  selectIsPaidBillingModel,
  selectNextResetLabel,
  selectPendingCreditRequest,
} from 'containers/Agents/creditSelectors'
import { TOAST_ADD, TOAST_REMOVE } from 'containers/Toasts/constants'

import CreditsExhaustedPaidToast from 'components/pages/resolutionCenter/Report/components/CreditsExhaustedPaidToast'
import CreditsExhaustedPlgToast from 'components/pages/resolutionCenter/Report/components/CreditsExhaustedPlgToast'

const ESCAPE_KEY = 'Escape'
const TOAST_ID = 'credit-request-toast'

/**
 * Controller for the admin "Team Member Requested Credits" toast. It does NOT
 * render a portal — it pushes the rich card into the shared Toasts container
 * (`ToastsContainer`) as a persistent `component` toast (bottom-right stack, no
 * 5s auto-dismiss). Visibility/dismissal logic lives here:
 *
 * - Admin/owner only, and only where billing applies (selectIsBillingDisabled) —
 *   non-admins never receive this (PLG §3.2 / Purchase §3.4).
 * - Dismissal advances a monotonic server cursor via
 *   `POST /members-api/credit-management/notifications/dismiss-popup { through_id }`;
 *   re-shows only when a newer request arrives (request_id > dismissed cursor).
 * - PLG vs paid card variant by billing model.
 */
const CreditRequestToastContainer = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const isAdminOrOwner = useSelector(selectIsAdminOrOwner)
  const isPaidBilling = useSelector(selectIsPaidBillingModel)
  const isBillingDisabled = useSelector(selectIsBillingDisabled)
  const billingStatus = useSelector(selectBillingStatus)
  const billingStatusError = useSelector(selectBillingStatusError)
  // Unknown tenantType falls back to PLG. Wait until billing/status resolves so a
  // PAYG admin is not shown the PLG "out of credits" card first.
  const billingStatusResolved = billingStatus != null || billingStatusError != null
  const pendingRequest = useSelector(selectPendingCreditRequest)
  const achievementsStats = useSelector(selectCompanyAchievements)
  const resetLabel = useSelector(selectNextResetLabel)

  // Highest request id dismissed this session (in-memory; the server cursor is
  // the durable source of truth).
  const [dismissedThroughId, setDismissedThroughId] = useState(null)

  const currentRequestId = pendingRequest?.request_id ?? null
  const isNewerThanDismissed =
    currentRequestId != null &&
    (dismissedThroughId == null || Number(currentRequestId) > Number(dismissedThroughId))

  // Both variants exist to drive an "Add credits" purchase — there is nothing to
  // buy on a tenant that is not billed, so the toast never shows there. This
  // covers POV (as before) plus billing bypass and DISABLE_PAYG_FF.
  const shouldShow =
    Boolean(pendingRequest) &&
    isAdminOrOwner &&
    !isBillingDisabled &&
    billingStatusResolved &&
    isNewerThanDismissed

  const dismissThrough = useCallback((requestId) => {
    if (requestId == null) return
    setDismissedThroughId(requestId)
    plgNotificationsAPI.dismissPopup(requestId).subscribe(
      () => {},
      () => {}
    )
  }, [])

  const handleClose = useCallback(() => {
    dismissThrough(currentRequestId)
  }, [dismissThrough, currentRequestId])

  const handleAddCredits = useCallback(() => {
    dismissThrough(currentRequestId)
    goToCreditsBilling(history)
  }, [dismissThrough, currentRequestId, history])

  // Esc dismisses the card (matches the X behaviour).
  useEffect(() => {
    if (!shouldShow) return undefined
    const onKeyDown = (event) => {
      if (event.key === ESCAPE_KEY) handleClose()
    }
    globalThis?.addEventListener?.('keydown', onKeyDown)
    return () => globalThis?.removeEventListener?.('keydown', onKeyDown)
  }, [shouldShow, handleClose])

  // Push/remove the card in the Toasts container as visibility changes.
  useEffect(() => {
    const remove = () => dispatch({ type: TOAST_REMOVE, toast: { id: TOAST_ID } })
    if (!shouldShow) {
      remove()
      return undefined
    }
    // Both variants are the admin-only credit-request toast. Paid tenants show
    // the achievements card (Figma 4584:149739, Purchase §3.4); PLG tenants show
    // the reset-date card (Figma 4584:149296, PLG §3.2).
    const Variant = isPaidBilling ? CreditsExhaustedPaidToast : CreditsExhaustedPlgToast
    const card = (
      <Variant
        requesterName={pendingRequest?.requester_name}
        otherRequestersCount={pendingRequest?.other_requesters_count ?? 0}
        resetLabel={resetLabel}
        achievementsStats={isPaidBilling ? achievementsStats : undefined}
        onClose={handleClose}
        onAddCredits={handleAddCredits}
      />
    )
    remove() // dedupe before (re)adding under the same id
    dispatch({ type: TOAST_ADD, toast: { id: TOAST_ID, persistent: true, component: card } })
    return remove
  }, [
    shouldShow,
    currentRequestId,
    isPaidBilling,
    resetLabel,
    achievementsStats,
    pendingRequest,
    handleClose,
    handleAddCredits,
    dispatch,
  ])

  return null
}

export default CreditRequestToastContainer
